import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { addNotification } from '../../store/slices/uiSlice';
import { API_URL } from '../../config/api';

const MESSAGE_TYPES = ['INFO', 'WARNING', 'SUCCESS', 'ERROR'];
const AUDIENCE_OPTIONS = [
  { value: 'ALL', label: 'All Workloop users' },
  { value: 'ORGANIZATIONS', label: 'Selected organizations' },
  { value: 'USERS', label: 'Selected users' },
];

const WorkloopBroadcastPanel = () => {
  const { token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [content, setContent] = useState('');
  const [messageType, setMessageType] = useState('INFO');
  const [audience, setAudience] = useState('ALL');
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [targetUsers, setTargetUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const authHeaders = token && token !== 'null' && token !== 'undefined'
    ? { Authorization: `Bearer ${token}` }
    : {};

  useEffect(() => {
    if (!authHeaders.Authorization) {
      setLoading(false);
      return;
    }
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [orgsRes, usersRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/admin/organizations`, { headers: authHeaders }),
        axios.get(`${API_URL}/admin/broadcast-targets/users`, { headers: authHeaders }),
        axios.get(`${API_URL}/admin/broadcasts`, { headers: authHeaders }),
      ]);
      setOrganizations(orgsRes.data || []);
      setTargetUsers(usersRes.data || []);
      setHistory(historyRes.data || []);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to load broadcast data',
      }));
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return targetUsers;
    return targetUsers.filter((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      return (
        name.includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    });
  }, [targetUsers, userSearch]);

  const recipientPreviewCount = useMemo(() => {
    if (audience === 'ALL') return targetUsers.length;
    if (audience === 'ORGANIZATIONS') {
      return targetUsers.filter((u) => selectedOrgIds.includes(u.organizationId)).length;
    }
    return selectedUserIds.length;
  }, [audience, targetUsers, selectedOrgIds, selectedUserIds]);

  const toggleOrg = (orgId) => {
    setSelectedOrgIds((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
    );
  };

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSend = async () => {
    if (!content.trim()) {
      dispatch(addNotification({ type: 'error', message: 'Message content is required' }));
      return;
    }
    if (audience === 'ORGANIZATIONS' && selectedOrgIds.length === 0) {
      dispatch(addNotification({ type: 'error', message: 'Select at least one organization' }));
      return;
    }
    if (audience === 'USERS' && selectedUserIds.length === 0) {
      dispatch(addNotification({ type: 'error', message: 'Select at least one user' }));
      return;
    }

    const preview = recipientPreviewCount;
    if (
      !window.confirm(
        `Send this broadcast to approximately ${preview} user(s)? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setSending(true);
      await axios.post(
        `${API_URL}/admin/broadcasts`,
        {
          content: content.trim(),
          type: messageType,
          audience,
          organizationIds: audience === 'ORGANIZATIONS' ? selectedOrgIds : undefined,
          userIds: audience === 'USERS' ? selectedUserIds : undefined,
        },
        { headers: authHeaders }
      );
      dispatch(addNotification({ type: 'success', message: 'Broadcast sent successfully' }));
      setContent('');
      setSelectedOrgIds([]);
      setSelectedUserIds([]);
      await loadData();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to send broadcast',
      }));
    } finally {
      setSending(false);
    }
  };

  const handleBackfill = async () => {
    if (!window.confirm('Create Workloop DM channels for all eligible users who do not have one yet?')) {
      return;
    }
    try {
      setBackfilling(true);
      const res = await axios.post(
        `${API_URL}/admin/workloop-channels/backfill`,
        {},
        { headers: authHeaders }
      );
      dispatch(addNotification({
        type: 'success',
        message: `Backfill complete. ${res.data.channelsCreated ?? 0} channel(s) created.`,
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Backfill failed',
      }));
    } finally {
      setBackfilling(false);
    }
  };

  const formatAudience = (item) => {
    if (item.audienceType === 'ALL') return 'All users';
    if (item.audienceType === 'ORGANIZATIONS') {
      return `Organizations (${(item.organizationIds || []).length})`;
    }
    return `Users (${(item.targetUserIds || []).length})`;
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-500 dark:text-gray-400">
        Loading broadcasts...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Compose broadcast
          </h2>
          <button
            type="button"
            onClick={handleBackfill}
            disabled={backfilling}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 disabled:opacity-50"
          >
            {backfilling ? 'Backfilling...' : 'Backfill Workloop channels'}
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Important platform announcement..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={messageType}
              onChange={(e) => setMessageType(e.target.value)}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {MESSAGE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Audience
            </label>
            <div className="flex flex-wrap gap-4">
              {AUDIENCE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="audience"
                    value={opt.value}
                    checked={audience === opt.value}
                    onChange={() => setAudience(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {audience === 'ORGANIZATIONS' && (
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
              {organizations.length === 0 ? (
                <p className="text-sm text-gray-500">No organizations found.</p>
              ) : (
                organizations.map((org) => (
                  <label key={org.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={selectedOrgIds.includes(org.id)}
                      onChange={() => toggleOrg(org.id)}
                    />
                    {org.organizationName} ({org.organizationSlug})
                  </label>
                ))
              )}
            </div>
          )}

          {audience === 'USERS' && (
            <div>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full mb-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
              <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
                {filteredUsers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                    />
                    {u.firstName} {u.lastName} (@{u.username})
                  </label>
                ))}
              </div>
            </div>
          )}

          <p className="text-sm text-gray-500 dark:text-gray-400">
            Estimated recipients: <strong>{recipientPreviewCount}</strong>
          </p>

          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {sending ? 'Sending...' : 'Send broadcast'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Broadcast history</h2>
          <button
            type="button"
            onClick={loadData}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Refresh
          </button>
        </div>
        {history.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400 text-sm">
            No broadcasts sent yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Audience</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {history.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {item.sentAt ? new Date(item.sentAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{item.messageType}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{formatAudience(item)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{item.recipientCount}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-md truncate">
                      {item.content}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkloopBroadcastPanel;
